/**
 * useProjectFeatures
 *
 * Provides a reactive helper to check whether the current project
 * has a given feature enabled, based on its event type.
 *
 * Usage in Options API components:
 *   import { hasFeature } from '@/composables/useProjectFeatures';
 *   methods: { hasFeature }
 *   template: v-if="hasFeature(scopedProjectData, 'stats_funnel')"
 *
 * Usage in Composition API:
 *   import { useProjectFeatures } from '@/composables/useProjectFeatures';
 *   const { hasFeature } = useProjectFeatures();
 */

/**
 * Check if a project has a specific feature enabled.
 *
 * @param {Object} projectData - The scopedProjectData object from Vuex
 * @param {string} featureSlug - The feature slug to check (e.g. 'stats_funnel')
 * @returns {boolean}
 */
export function hasFeature(projectData, featureSlug) {
    if (!projectData || !Array.isArray(projectData.features)) {
        return false;
    }
    return projectData.features.includes(featureSlug);
}

/**
 * Check if a project has ALL of the given features.
 *
 * @param {Object} projectData
 * @param {string[]} featureSlugs
 * @returns {boolean}
 */
export function hasAllFeatures(projectData, featureSlugs) {
    return featureSlugs.every((slug) => hasFeature(projectData, slug));
}

/**
 * Check if a project has ANY of the given features.
 *
 * @param {Object} projectData
 * @param {string[]} featureSlugs
 * @returns {boolean}
 */
export function hasAnyFeature(projectData, featureSlugs) {
    return featureSlugs.some((slug) => hasFeature(projectData, slug));
}

/**
 * Composition API wrapper.
 * Returns the same helpers bound to the reactive project data.
 */
export function useProjectFeatures() {
    return {
        hasFeature,
        hasAllFeatures,
        hasAnyFeature,
    };
}
